import type { Note } from '@/app/db/repo';
import {
  Archive,
  FolderPlus,
  LogOut,
  Monitor,
  Moon,
  Settings,
  Sun,
} from 'lucide-react';
import {
  type MouseEvent,
  useRef,
  useState,
} from 'react';
import { Backup } from './Backup';
import { PopupMenu } from './PopupMenu';
import {
  savedTheme,
  saveTheme,
  type Theme,
} from './theme';

const THEMES = [
  { theme: 'system', label: 'System', Icon: Monitor },
  { theme: 'light', label: 'Light', Icon: Sun },
  { theme: 'dark', label: 'Dark', Icon: Moon },
] as const;

type Open = { bottom: number, right: number, focusFirst: boolean };

// the app's own actions, in the sidebar's footer, so the menu grows upwards
export const SettingsMenu = (
  { notes, onNewFolder, onSignOut }: {
    notes: () => Promise<Note[]>,
    onNewFolder: () => void,
    onSignOut: () => void,
  },
) => {
  const [open, setOpen] = useState<Open | null>(null);
  const [backup, setBackup] = useState(false);
  const [theme, setTheme] = useState<Theme>(savedTheme);
  const button = useRef<HTMLButtonElement>(null);

  const close = (refocus = false) => {
    setOpen(null);
    if (refocus) button.current?.focus();
  };

  // a click with no pointer behind it came from the keyboard
  const toggle = (e: MouseEvent) => {
    if (open || !button.current) return close();
    const r = button.current.getBoundingClientRect();
    setOpen({
      bottom: window.innerHeight - r.top + 4,
      right: window.innerWidth - r.right,
      focusFirst: e.detail === 0,
    });
  };

  const run = (action: () => void) => () => {
    close();
    action();
  };

  return (
    <>
      <button
        ref={button}
        className="menu-button settings"
        title="Settings"
        aria-label="Settings"
        aria-haspopup="menu"
        aria-expanded={Boolean(open)}
        onClick={toggle}>
        <Settings aria-hidden="true" />
      </button>
      {open && (
        <PopupMenu
          at={{
            bottom: open.bottom,
            right: open.right,
            transformOrigin: 'bottom right',
          }}
          focusFirst={open.focusFirst}
          onClose={close}
          opener={button}>
          {THEMES.map(({ theme: t, label, Icon }) => (
            <button
              key={t}
              role="menuitemradio"
              aria-checked={theme === t}
              onClick={run(() => {
                saveTheme(t);
                setTheme(t);
              })}>
              <span className="icon">
                <Icon aria-hidden="true" />
              </span>
              {label}
            </button>
          ))}
          <hr />
          <button role="menuitem" onClick={run(onNewFolder)}>
            <span className="icon">
              <FolderPlus aria-hidden="true" />
            </span>
            New folder…
          </button>
          <button role="menuitem" onClick={run(() => setBackup(true))}>
            <span className="icon">
              <Archive aria-hidden="true" />
            </span>
            Daily backup…
          </button>
          <button role="menuitem" onClick={run(onSignOut)}>
            <span className="icon">
              <LogOut aria-hidden="true" />
            </span>
            Sign out
          </button>
        </PopupMenu>
      )}
      {backup && (
        <Backup
          notes={notes}
          onClose={() => {
            setBackup(false);
            button.current?.focus();
          }} />
      )}
    </>
  );
};
